import { sentryService } from '@/services/telemetry/sentry';
import type {
  GoPhotoUploadResponse,
  GoPhotoUploadResult,
} from '@/shared/types/api';
import { silently, track } from '@/shared/utils/track-telemetry';

interface UploadStartContext {
  gophotoCode: string;
  isRetry: boolean;
  retryCount: number;
}

interface UploadBatchContext {
  imageUuids: string[];
  isRetry: boolean;
}

interface AutoRetryContext {
  allFailed: boolean;
  attempt: number;
  delay: number;
  failedUuids: string[];
  maxRetries: number;
}

interface ExhaustedFailureContext {
  allFailed: boolean;
  attempt: number;
  failedResults: GoPhotoUploadResult[];
  failedUuids: string[];
  gophotoCode: string;
  retryCount: number;
  successCount: number;
  totalCount: number;
}

interface SuccessContext {
  duration: number;
  retryCount: number;
  totalCount: number;
}

interface ExceptionAttemptContext {
  attempt: number;
  duration: number;
  error: unknown;
}

interface ExhaustedExceptionContext extends ExceptionAttemptContext {
  gophotoCode: string;
  imageUuids: string[];
  retryCount: number;
}

interface PartialAcceptedContext {
  failedCount: number;
  successCount: number;
  totalCount: number;
}

function errorMessage(error: unknown): string | undefined {
  return (error as { message?: string } | null)?.message;
}

function warnCaptureFailure(label: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: upload telemetry must never block the kiosk flow.
  console.warn(label, errorMessage(error));
}

export function trackUploadStarted({
  gophotoCode,
  isRetry,
  retryCount,
}: UploadStartContext): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb(
        'gophoto.upload',
        isRetry ? 'GoPhoto upload retry started' : 'GoPhoto upload started',
        { isRetry, retryCount, gophotoCode }
      );
    },
    warnCaptureFailure
  );
}

export function trackValidationFailure(message: string): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb('gophoto.upload', message, {}, 'warning');
    },
    warnCaptureFailure
  );
}

export function trackUploadBatch({
  imageUuids,
  isRetry,
}: UploadBatchContext): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb('gophoto.upload', 'Processing upload batch', {
        totalImages: imageUuids.length,
        uniqueImages: new Set(imageUuids).size,
        imageUuids: [...new Set(imageUuids)],
        isRetry,
      });
    },
    warnCaptureFailure
  );
}

export function withUploadProcessSpan<T>(
  context: UploadBatchContext & { retryCount: number },
  action: () => Promise<T>
): Promise<T> {
  return sentryService.withSpan(
    'gophoto.upload.process',
    'gophoto.upload',
    action,
    {
      totalImages: context.imageUuids.length,
      uniqueImages: new Set(context.imageUuids).size,
      isRetry: context.isRetry,
      retryCount: context.retryCount,
    }
  );
}

export function withTransferSpan<T>(
  imageCount: number,
  action: () => Promise<T>
): Promise<T> {
  return sentryService.withSpan(
    'gophoto.upload.transfer',
    'gophoto.transfer',
    action,
    { imageCount }
  );
}

export function trackTransferStarted(imageCount: number): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.transfer',
      'Starting GoPhoto transfer',
      { imageCount }
    );
  });
}

export function trackTransferCompleted(
  result: GoPhotoUploadResponse,
  imageCount: number,
  duration: number
): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.transfer',
      'GoPhoto transfer completed',
      {
        successfulUploads: result.successful_uploads,
        failedUploads: result.failed_uploads,
        durationMs: duration,
      }
    );
    sentryService.trackMetric(
      'gophoto.transfer.duration',
      duration,
      'millisecond',
      {
        imageCount,
        successCount: result.successful_uploads,
      }
    );
  });
}

export function trackBackgroundAccepted(
  imageCount: number,
  transferDuration: number
): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.upload',
      'Background upload accepted, dismissing visitor',
      { totalImages: imageCount, transferDurationMs: transferDuration }
    );
    sentryService.incrementCounter('gophoto.upload.background_accepted', 1, {
      imageCount,
    });
  });
}

export function trackAutoRetry({
  allFailed,
  attempt,
  delay,
  failedUuids,
  maxRetries,
}: AutoRetryContext): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.upload',
      `Auto-retry ${attempt}/${maxRetries} for ${failedUuids.length} failed`,
      {
        autoRetryAttempt: attempt,
        failedCount: failedUuids.length,
        failedImageUuids: failedUuids,
        delayMs: delay,
        allFailed,
      }
    );
  });
}

export function trackExhaustedFailures({
  allFailed,
  attempt,
  failedResults,
  failedUuids,
  gophotoCode,
  retryCount,
  successCount,
  totalCount,
}: ExhaustedFailureContext): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.upload',
      allFailed
        ? 'All uploads failed after auto-retries'
        : 'Partial failure after auto-retries',
      {
        failedCount: failedUuids.length,
        failedImageUuids: failedUuids,
        successCount,
        autoRetryAttempt: attempt,
        errors: failedResults.map((result) => result.error).slice(0, 5),
      },
      allFailed ? 'error' : 'warning'
    );

    sentryService.captureException(
      new Error(
        allFailed
          ? `GoPhoto upload failed - all ${failedResults.length} images failed after ${attempt} auto-retries`
          : `GoPhoto upload partial failure - ${failedUuids.length} of ${totalCount} failed after ${attempt} auto-retries`
      ),
      {
        tags: {
          component: 'GoPhotoUpload',
          uploadResult: allFailed ? 'all_failed' : 'partial_failure',
        },
        extra: {
          failedCount: failedUuids.length,
          failedImageUuids: failedUuids,
          successCount,
          totalCount,
          autoRetryAttempt: attempt,
          gophotoCode,
          retryCount,
        },
        level: allFailed ? 'error' : 'warning',
      }
    );

    sentryService.incrementCounter(
      allFailed
        ? 'gophoto.upload.all_failed'
        : 'gophoto.upload.partial_success',
      1,
      { failedCount: failedUuids.length, successCount }
    );

    if (!allFailed) {
      sentryService.trackMetric(
        'gophoto.upload.success_rate',
        (successCount / totalCount) * 100,
        'percent',
        { totalImages: totalCount }
      );
    }
  });
}

export function trackUploadSuccess({
  duration,
  retryCount,
  totalCount,
}: SuccessContext): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.upload',
      'Upload completed successfully',
      { totalImages: totalCount, durationMs: duration, retryCount }
    );
    sentryService.incrementCounter('gophoto.upload.success', 1, {
      imageCount: totalCount,
    });
    sentryService.trackMetric(
      'gophoto.upload.total_duration',
      duration,
      'millisecond',
      { imageCount: totalCount, retryCount }
    );
    sentryService.trackMetric('gophoto.upload.success_rate', 100, 'percent', {
      totalImages: totalCount,
    });
  });
}

export function trackUploadExceptionAttempt({
  attempt,
  duration,
  error,
}: ExceptionAttemptContext): void {
  silently(() => {
    sentryService.addBreadcrumb(
      'gophoto.upload',
      'Upload process threw exception',
      {
        error: errorMessage(error),
        durationMs: duration,
        autoRetryAttempt: attempt,
      },
      'error'
    );
    sentryService.incrementCounter('gophoto.upload.exception', 1);
  });
}

export function trackExhaustedException({
  attempt,
  duration,
  error,
  gophotoCode,
  imageUuids,
  retryCount,
}: ExhaustedExceptionContext): void {
  silently(() => {
    sentryService.captureException(error, {
      tags: { component: 'GoPhotoUpload', uploadResult: 'exception' },
      extra: {
        gophotoCode,
        totalImages: imageUuids.length,
        imageUuids,
        retryCount,
        autoRetryAttempt: attempt,
        durationMs: duration,
      },
      level: 'error',
    });
  });
}

export function trackManualFullRetry(retryCount: number): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb(
        'gophoto.upload',
        'User initiated full retry',
        { previousRetryCount: retryCount }
      );
      sentryService.incrementCounter('gophoto.upload.retry_full', 1);
    },
    warnCaptureFailure
  );
}

export function trackManualPartialRetry(
  failedCount: number,
  retryCount: number
): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb(
        'gophoto.upload',
        'User initiated partial retry',
        { failedCount, previousRetryCount: retryCount }
      );
      sentryService.incrementCounter('gophoto.upload.retry_partial', 1, {
        failedCount,
      });
    },
    warnCaptureFailure
  );
}

export function trackPartialAccepted({
  failedCount,
  successCount,
  totalCount,
}: PartialAcceptedContext): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb(
        'gophoto.upload',
        'User accepted partial success',
        { successCount, failedCount, totalCount }
      );
      sentryService.incrementCounter('gophoto.upload.partial_accepted', 1, {
        successCount,
        failedCount,
      });
    },
    warnCaptureFailure
  );
}

export function trackKioskReset(
  successCount: number,
  retryCount: number
): void {
  track(
    '[GoPhotoUpload] Sentry capture failed:',
    () => {
      sentryService.addBreadcrumb(
        'gophoto.upload',
        'GoPhoto upload flow completed, resetting kiosk',
        { finalSuccessCount: successCount, totalRetries: retryCount }
      );
    },
    warnCaptureFailure
  );
}
